import { FontFamily } from '../../components/Font/fontConstants';
import { BackgroundColor } from '../../constants';
import { defaultMotifWhite } from '../defaultMotifWhite';
import { m } from '../m';
import { MotifComponent, MotifScheme } from '../motifConstants';
import type { Motif } from '../motifTypes';
import { mergeMotifs } from '../motifUtils';

// TODO: export these values from SDS-M
const snapNeutralColors = {
  '--palette-gray-v50': '#F8F8F8',
  '--palette-gray-v100': '#F1F1F1',
  '--palette-gray-v150': '#D9D9D9',
  '--palette-gray-v200': '#BFBFBF',
  '--palette-gray-v250': '#A6A6A6',
  '--palette-gray-v300': '#8C8C8C',
  '--palette-black-v50': '#757575',
  '--palette-black-v100': '#595959',
  '--palette-black-v125': '#404040',
  '--palette-black-v150': '#262626',
};

/** Motif for the Snap.com homepage */
export const snapMotif: Motif = mergeMotifs(defaultMotifWhite, {
  name: 'Snap',
  // we need to manually override this later because the merge function does not replace the array, it merges them.
  fontFamily: [FontFamily.INTER],
  [MotifScheme.DEFAULT]: {
    legacyName: BackgroundColor.White,
    name: 'Snap White background',
    [MotifComponent.ROOT]: {
      ...snapNeutralColors,
      '--font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      // H1
      '--h1-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h1-desktop-font-size': '56px',
      '--h1-desktop-font-size-compact': '48px',
      '--h1-desktop-font-line-height': '120%',
      '--h1-desktop-font-weight': '500',
      '--h1-desktop-font-stretch': 'normal',
      '--h1-desktop-font-text-transform': 'none',
      '--h1-mobile-font-size': '32px',
      '--h1-mobile-font-line-height': '120%',
      '--h1-mobile-font-weight': '500',
      '--h1-mobile-font-stretch': 'normal',
      '--h1-mobile-font-text-transform': 'none',
      // H2
      '--h2-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h2-desktop-font-size': '36px',
      '--h2-desktop-font-line-height': '120%',
      '--h2-desktop-font-weight': '500',
      '--h2-desktop-font-stretch': 'normal',
      '--h2-desktop-font-text-transform': 'none',
      '--h2-desktop-font-letter-spacing': '0',
      '--h2-mobile-font-size': '28px',
      '--h2-mobile-font-line-height': '120%',
      '--h2-mobile-font-weight': '500',
      '--h2-mobile-font-stretch': 'normal',
      '--h2-mobile-font-text-transform': 'none',
      '--h2-mobile-font-letter-spacing': '0',
      // H3
      '--h3-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h3-desktop-font-size': '28px',
      '--h3-desktop-font-weight': '500',
      '--h3-mobile-font-size': '24px',
      '--h3-mobile-font-weight': '500',
      // H4
      '--h4-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h4-desktop-font-size': '22px',
      '--h4-desktop-font-weight': '500',
      '--h4-mobile-font-size': '22px',
      '--h4-mobile-font-weight': '500',
      // H5
      '--h5-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h5-desktop-font-size': '20px',
      '--h5-desktop-font-weight': '500',
      '--h5-mobile-font-size': '18px',
      '--h5-mobile-font-weight': '500',
      // H6
      '--h6-font-family': `${FontFamily.INTER}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h6-desktop-font-weight': '500',
      '--h6-mobile-font-size': '16px',
      '--h6-mobile-font-weight': '500',
      // action colors
      '--action-text-hover-color': m('--neutral-v700'),
      '--action-indicator-hover-opacity': '0',
      // override the gutter color to be white
      '--gutter-color': m('--neutral-v100'),
    },
    [MotifComponent.HEADER]: {
      '--global-header-bg-color': m('--neutral-v0'),
      '--global-header-border-color': m('--neutral-v700'),
      '--global-header-nav-screen-global-links-bg-color': m('--neutral-v0'),
      '--global-header-navigator-item-hover-color': m('--action-text-hover-color'),
      '--global-header-navigator-item-desktop-font-text-transform': 'uppercase',
      '--global-header-navigator-item-mobile-font-text-transform': 'none',
    },
    [MotifComponent.FOOTER]: {
      '--footer-bg-color': m('--neutral-v0'),
      '--footer-bar-bg-color': m('--neutral-v0'),
      '--footer-bar-header-desktop-font-text-transform': 'uppercase',
      '--footer-bar-header-mobile-font-text-transform': 'uppercase',
      '--footer-bar-header-font-weight': '600',
      '--footer-border-color': m('--neutral-v700'),
      '--footer-divider-border-color': m('--neutral-v700'),
      '--footer-bar-divider-border-color': m('--neutral-v700'),
    },
    [MotifComponent.MEDIA]: {
      '--media-border-radius': '0px',
    },
    [MotifComponent.BUTTON]: {
      '--button-border-width': '2px',
      '--button-primary-border-color': m('--neutral-v500'),
      '--button-primary-hover-border-color': m('--neutral-v700'),
      '--button-primary-bg-color': m('--neutral-v0'),
      '--button-primary-hover-bg-color': m('--neutral-v700'),
      '--button-primary-hover-fg-color': m('--neutral-v0'),

      '--button-secondary-bg-color': m('--neutral-v150'),
      '--button-secondary-hover-bg-color': m('--palette-gray-v200'),
      '--button-secondary-border-color': m('--neutral-v150'),
      '--button-secondary-hover-border-color': m('--palette-gray-v200'),
      '--button-secondary-hover-fg-color': m('--neutral-v700'),
      '--button-secondary-fg-color': m('--neutral-v700'),
      '--button-flat-fg-color': m('--neutral-v0'),
    },
    [MotifComponent.CAROUSEL]: {
      '--carousel-active-dot-color': m('--neutral-v700'),
      '--carousel-card-border-radius': '0',
      '--carousel-card-box-shadow': 'none',
      '--carousel-card-hover-box-shadow': 'none',
      '--carousel-card-desktop-grid-gap': '32px',
      '--carousel-card-mobile-grid-gap': '16px',
      '--carousel-card-border-color': 'transparent',
      '--carousel-card-hover-border-color': 'transparent',
      '--carousel-card-bg-color': 'transparent',
      '--carousel-card-hover-bg-color': 'transparent',
      '--carousel-inactive-dot-color': 'rgba(133, 141, 148, 0.50)',
      '--carousel-card-desktop-text-align': 'center',
      '--carousel-card-desktop-text-padding': `${m('--spacing-m')} 0`,
      '--carousel-card-mobile-text-padding': `${m('--spacing-m')} 0`,
      '--carousel-card-border-width': '0px',
    },
    [MotifComponent.SIDE_NAVIGATION]: {
      '--side-navigation-desktop-bar-bg-color': m('--neutral-v0'),
      '--side-navigation-desktop-border-color': m('--neutral-v700'),
      '--side-navigation-desktop-border-width': '1px',
      '--side-navigation-right-padding': m('--spacing-xl'),
      '--side-navigation-left-padding': m('--spacing-xxxl'),
    },
    [MotifComponent.FOOTNOTE]: {
      '--footnote-border-color': m('--neutral-v700'),
    },
    [MotifComponent.DROPDOWN_MENU]: {
      '--dropdown-button-border-radius': m('--border-radius-xl'),
      '--dropdown-button-border-width': m('--border-width-s'),
      '--dropdown-button-border-color': m('--neutral-v700'),
      '--dropdown-button-hover-border-color': m('--neutral-v700'),
    },
    [MotifComponent.TOGGLE_SLIDER]: {
      '--toggle-slider-bg-color': m('--neutral-v400'),
      '--toggle-slider-active-color': m('--neutral-v700'),
    },
    [MotifComponent.COOKIE_MODAL_SETTINGS]: {
      '--cookie-modal-body-inline-padding': '0px',
      '--cookie-modal-border-color': m('--fg-color'),
      '--cookie-modal-border-radius': '0px',
      '--cookie-modal-border-width': m('--border-width-xs'),
      '--cookie-modal-box-shadow': m('--box-shadow-l'),
      '--cookie-modal-logo-opacity': '0',
      '--cookie-modal-min-width': '580px',
      '--cookie-modal-desktop-vertical-padding': m('--spacing-l'),
      '--cookie-modal-desktop-horizontal-padding': m('--spacing-l'),
      '--cookie-modal-mobile-vertical-padding': m('--spacing-s'),
      '--cookie-modal-mobile-horizontal-padding': m('--spacing-s'),
      '--cookie-modal-rich-text-title-desktop-font-size': m('--h6-desktop-font-size'),
      '--cookie-modal-rich-text-title-desktop-font-line-height': m('--h6-desktop-font-line-height'),
      '--cookie-modal-rich-text-title-desktop-font-weight': m('--h6-desktop-font-weight'),
      '--cookie-modal-rich-text-title-desktop-font-stretch': m('--h6-desktop-font-stretch'),
      '--cookie-modal-rich-text-title-mobile-font-size': m('--h6-mobile-font-size'),
      '--cookie-modal-rich-text-title-mobile-font-line-height': m('--h6-mobile-font-line-height'),
      '--cookie-modal-rich-text-title-mobile-font-weight': m('--h6-mobile-font-weight'),
      '--cookie-modal-rich-text-title-mobile-font-stretch': m('--h6-mobile-font-stretch'),
      '--cookie-modal-rich-text-base-desktop-font-size': m('--p3-desktop-font-size'),
      '--cookie-modal-rich-text-base-desktop-font-line-height': m('--p3-desktop-font-line-height'),
      '--cookie-modal-rich-text-base-desktop-font-weight': m('--p3-desktop-font-weight'),
      '--cookie-modal-rich-text-base-desktop-font-stretch': m('--p3-desktop-font-stretch'),
      '--cookie-modal-rich-text-base-mobile-font-size': m('--p3-mobile-font-size'),
      '--cookie-modal-rich-text-base-mobile-font-line-height': m('--p3-mobile-font-line-height'),
      '--cookie-modal-rich-text-base-mobile-font-weight': m('--p3-mobile-font-weight'),
      '--cookie-modal-rich-text-base-mobile-font-stretch': m('--p3-mobile-font-stretch'),
      '--cookie-settings-box-shadow': 'none',
      '--cookie-settings-max-width': '800px',
      '--cookie-settings-border-width': m('--border-width-none'),
      '--cookie-settings-padding': m('--spacing-l'),
      '--cookie-settings-category-border-color': m('--neutral-v250'),
      '--cookie-settings-category-border-radius': '0px',
      '--cookie-settings-category-border-width': m('--border-width-xs'),
      '--cookie-settings-category-padding': m('--spacing-s'),
      '--cookie-settings-category-title-desktop-font-size': m('--h6-desktop-font-size'),
      '--cookie-settings-category-title-desktop-font-line-height': m(
        '--h6-desktop-font-line-height'
      ),
      '--cookie-settings-category-title-desktop-font-weight': m('--h6-desktop-font-weight'),
      '--cookie-settings-category-title-desktop-font-stretch': m('--h6-desktop-font-stretch'),
      '--cookie-settings-category-title-mobile-font-size': m('--h6-mobile-font-size'),
      '--cookie-settings-category-title-mobile-font-line-height': m('--h6-mobile-font-line-height'),
      '--cookie-settings-category-title-mobile-font-weight': m('--h6-mobile-font-weight'),
      '--cookie-settings-category-title-mobile-font-stretch': m('--h6-mobile-font-stretch'),
      '--cookie-settings-category-description-desktop-font-size': m('--p2-desktop-font-size'),
      '--cookie-settings-category-description-desktop-font-line-height': m(
        '--p2-desktop-font-line-height'
      ),
      '--cookie-settings-category-description-desktop-font-stretch': m('--p2-desktop-font-stretch'),
      '--cookie-settings-category-description-desktop-font-weight': m('--p2-desktop-font-weight'),
      '--cookie-settings-category-description-mobile-font-size': m('--p2-mobile-font-size'),
      '--cookie-settings-category-description-mobile-font-line-height': m(
        '--p2-mobile-font-line-height'
      ),
      '--cookie-settings-category-description-mobile-font-stretch': m('--p2-mobile-font-stretch'),
      '--cookie-settings-category-description-mobile-font-weight': m('--p2-mobile-font-weight'),
    },
  },
});

// override the font family to use Inter
snapMotif.fontFamily = [FontFamily.INTER];
